import { Button, IconButton, Modal, Stack, TextField, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { createRootStyle } from '../styles';
import PublicAPI from '@/api/TreedisPublicDAO';
import { User } from '@/_models/User';

export function getTokenData(token?: string) {
  // Split the token into its parts
  if (!token) {
    return { tokenExpired: true };
  }
  const payloadEncoded = token.split('.')[1];

  if (!payloadEncoded) {
    return { tokenExpired: true };
  }

  // Decode the payload
  const payloadDecoded = atob(payloadEncoded);

  // Parse the payload as JSON
  const payload = JSON.parse(payloadDecoded);

  // Check if the exp property exists
  if (!payload.exp) {
    return { tokenExpired: true };
  }

  // Get the current date/time in seconds since the Unix epoch
  const currentDate = Math.floor(Date.now() / 1000);

  // Check if the current date/time is greater than the exp property
  return { tokenExpired: currentDate > payload.exp, userIdFromToken: payload.userId };
}

const getReCaptchaToken = () => {
  return new Promise((resolve, reject) => {
    if (!process.env.NEXT_PUBLIC_RECAPTCHA_KEY || process.env.NODE_ENV !== 'production') resolve(null);
    window.grecaptcha.ready(() => {
      window.grecaptcha.execute(process.env.NEXT_PUBLIC_RECAPTCHA_KEY, { action: 'main' }).then(resolve).catch(reject);
    });
  });
};

export const ThreadLoginPopup = ({
  collaborators,
  userId,
  setUserId,
  setIsGuest,
  tour,
}: {
  collaborators: User[];
  userId: number | null;
  setUserId: (userId: number) => void;
  setIsGuest: (value: boolean) => void;
  tour: any;
}) => {
  const [isOpen, setIsOpen] = useState(!localStorage.getItem('userToken'));
  const [inputs, setInputs] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);

  const handleInputsChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;

    setInputs((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!inputs.email || !inputs.password) {
      toast.error('Please fill in credentials');
      return;
    }

    if (!collaborators?.find((c) => c.email === inputs.email)) {
      toast.error('You are not a collaborator on this space');
      return;
    }

    const captchaToken = await getReCaptchaToken();
    const res = await PublicAPI.login({ ...inputs, captchaToken });

    if (res) {
      const { data } = res;
      if (data) {
        localStorage.setItem('userToken', data.refreshToken); // refreshToken because it's long lived

        setUserId(Number(data.user.id));
        setIsOpen(false);
      } else {
        toast.error(res.message);
      }
    } else {
      toast.error('Wrong password or email');
    }
  };

  useEffect(() => {
    const getSlugFromUrl = () => {
      const segments = window.location.href.split('/');
      return segments[segments.length - 1].split('?')[0];
    };

    const slug = getSlugFromUrl();

    if (tour?.slug && slug !== tour.slug) {
      const token = localStorage.getItem('userToken');
      const { userIdFromToken } = getTokenData(token);
      if (!collaborators.map((c) => c.id).includes(Number(userIdFromToken))) {
        setIsOpen(true);
      }
    }
  }, [window.location.href, tour?.slug]);

  useEffect(() => {
    const token = localStorage.getItem('userToken');
    const { tokenExpired, userIdFromToken } = getTokenData(token);
    if ((tokenExpired || !token) && !userId) {
    }
    if (!collaborators.map((c) => c.id).includes(Number(userIdFromToken))) {
      setIsOpen(true);
    }
    if (!userIdFromToken) {
      setUserId(null);
    } else {
      setUserId(Number(userIdFromToken));
    }
  }, [userId, setUserId, collaborators]);

  const handleEnterPress = (event) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Modal
      open={isOpen}
      hideBackdrop
      sx={{
        background: 'rgba(0, 0, 0, 0.4)',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(15px)',
      }}
      onKeyDown={handleEnterPress}
    >
      <Stack direction="column" alignItems="center" justifyContent="center" gap={{ xs: 2, md: 3 }} sx={createRootStyle()}>
        <TextField
          required
          autoFocus
          id="email"
          type="email"
          label="Email"
          value={inputs.email}
          onChange={handleInputsChange}
          sx={{
            '& .MuiInputBase-root , input': {
              height: { xs: '36px', md: '56px' },
              lineHeight: { xs: '36px', md: '56px' },
            },
          }}
          inputProps={{
            style: {
              width: '320px',
              borderColor: '#FFFFFF',
              borderRadius: '4px',
              color: 'white',
            },
          }}
          InputLabelProps={{ sx: { top: { xs: '-10px', md: 0 }, '&.MuiInputLabel-shrink': { top: 0 } } }}
        />
        <TextField
          required
          id="password"
          type={showPassword ? 'text' : 'password'}
          label="Password"
          value={inputs.password}
          onChange={handleInputsChange}
          sx={{
            '& .MuiInputBase-root , input': {
              height: { xs: '36px', md: '56px' },
              lineHeight: { xs: '36px', md: '56px' },
              width: '320px',
            },
          }}
          InputProps={{
            endAdornment: (
              <IconButton onClick={() => setShowPassword((prevState) => !prevState)} sx={{ color: '#FFF' }}>
                {showPassword ? <i className="fa-regular fa-eye" /> : <i className="fa-regular fa-eye-slash" />}
              </IconButton>
            ),
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputProps={{
            style: {
              borderColor: '#FFFFFF',
              borderRadius: '4px',
              color: 'white',
            },
          }}
          InputLabelProps={{ sx: { top: { xs: '-10px', md: 0 }, '&.MuiInputLabel-shrink': { top: 0 } } }}
        />
        <Stack gap={1} alignItems="center" width="320px">
          <Button
            type="submit"
            onClick={handleSubmit}
            sx={{
              backgroundColor: '#2055FF',
              my: { xs: 0, lg: '5px' },
              height: '56px',
              width: { xs: '100%' },
            }}
          >
            Login as Collaborator
          </Button>
          <Button
            onClick={() => {
              setIsOpen(false);
              setIsGuest(true);
            }}
            sx={{
              backgroundColor: '#2055FF',
              my: { xs: 0, lg: '5px' },
              height: '56px',
              width: { xs: '100%' },
            }}
          >
            Enter as Guest
          </Button>
          <Typography variant="caption" color="#fff">
            Only Collaborators can add Threads and leave comments. If you don't have access as a collaborator on this space, please contact the admin.
          </Typography>
        </Stack>
      </Stack>
    </Modal>
  );
};
